import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@/components/ui/link";
import { useParams } from "@/lib/navigation";
import * as React from "react";

import { CredentialDialog, type Credential } from "@/components/credential-dialog";
import { TenantGoogleSso } from "@/components/tenant-google-sso";
import { PageHeader } from "@/components/layout-bits";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  StatusBadge,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { activateTenant, issueTenantAdmin, suspendTenant } from "@/lib/api/endpoints";
import { useTenant } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import { formatDate } from "@/lib/utils";

export default function TenantDetailPage() {
  // Was a Next server-passed `params` prop; React Router supplies the segment
  // through the hook instead. The route path (`/tenants/:slug`) guarantees it.
  const slug = String(useParams().slug);
  const qc = useQueryClient();
  const { data: tenant, isLoading, isError, error, refetch } = useTenant(slug);
  const [credential, setCredential] = React.useState<Credential | null>(null);
  const [email, setEmail] = React.useState("");

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: qk.tenant(slug) }),
      qc.invalidateQueries({ queryKey: qk.tenants }),
    ]);

  const suspend = useMutation({ mutationFn: () => suspendTenant(slug), onSuccess: invalidate });
  const activate = useMutation({ mutationFn: () => activateTenant(slug), onSuccess: invalidate });
  const issue = useMutation({
    mutationFn: () => issueTenantAdmin(slug, email),
    onSuccess: (res) => {
      setCredential({ email: res.email, password: res.temp_password });
      setEmail("");
    },
  });

  return (
    <div className="max-w-3xl">
      <PageHeader
        title={tenant?.display_name ?? slug}
        description="Tenant details and administration."
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link href="/tenants">← All tenants</Link>
          </Button>
        }
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading || !tenant ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Details</CardTitle>
              <StatusBadge status={tenant.status} />
            </CardHeader>
            <CardContent className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              <Detail
                label="Slug"
                value={<code className="font-mono text-xs">{tenant.slug}</code>}
              />
              <Detail
                label="Database"
                value={<code className="font-mono text-xs">{tenant.db_name}</code>}
              />
              <Detail label="Region" value={tenant.region ?? "—"} />
              <Detail label="Provisioned" value={formatDate(tenant.provisioned_at)} />
            </CardContent>
            <div className="flex items-center gap-2 px-6 pb-6">
              {tenant.status === "active" && (
                <Button
                  variant="destructive"
                  disabled={suspend.isPending}
                  onClick={() => suspend.mutate()}
                >
                  {suspend.isPending ? "Suspending…" : "Suspend"}
                </Button>
              )}
              {tenant.status === "suspended" && (
                <Button
                  variant="accent"
                  disabled={activate.isPending}
                  onClick={() => activate.mutate()}
                >
                  {activate.isPending ? "Activating…" : "Re-activate"}
                </Button>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Issue an admin credential</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (email.trim()) issue.mutate();
                }}
              >
                <div className="min-w-[240px] flex-1 space-y-1.5">
                  <Label htmlFor="admin-email">Email</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder="admin@tenant.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={issue.isPending || !email.trim()}>
                  {issue.isPending ? "Issuing…" : "Issue credential"}
                </Button>
              </form>
              <p className="mt-2 text-xs text-muted-foreground">
                Creates or resets an admin for this tenant and returns a one-time temporary
                password.
              </p>
            </CardContent>
          </Card>

          <TenantGoogleSso slug={slug} />
        </div>
      )}

      <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-foreground">{value}</div>
    </div>
  );
}
